import { useState, useEffect, useRef } from "react";

interface StreamingRenderState {
  showFirstCards: boolean;
  renderedItineraryDays: number;
  renderedItinerarySchedules: { [key: number]: number };
  showSecondCards: boolean;
  showAttractionsTitle: boolean;
  renderedAttractions: number;
  showThirdCards: boolean;
  showSeasonalHighlightsTitle: boolean;
  renderedSeasonalHighlights: number;
  showFourthCards: boolean;
  showLocalEventsTitle: boolean;
  renderedLocalEvents: number;
  showFifthCards: boolean;
  showTravelTipsTitle: boolean;
  renderedTravelTips: number;
  renderedTravelBooking: number;
  showTravelBookingTitle: boolean;
  showBackButton: boolean;
}

export function useStreamingRender(planData: any): StreamingRenderState {
  // 流式渲染状态
  const [showFirstCards, setShowFirstCards] = useState(false);
  const [renderedItineraryDays, setRenderedItineraryDays] = useState(0);
  const [renderedItinerarySchedules, setRenderedItinerarySchedules] = useState<{
    [key: number]: number;
  }>({});
  const [showSecondCards, setShowSecondCards] = useState(false);
  const [showAttractionsTitle, setShowAttractionsTitle] = useState(false);
  const [renderedAttractions, setRenderedAttractions] = useState(0);
  const [showThirdCards, setShowThirdCards] = useState(false);
  const [showSeasonalHighlightsTitle, setShowSeasonalHighlightsTitle] =
    useState(false);
  const [renderedSeasonalHighlights, setRenderedSeasonalHighlights] =
    useState(0);
  const [showFourthCards, setShowFourthCards] = useState(false);
  const [showLocalEventsTitle, setShowLocalEventsTitle] = useState(false);
  const [renderedLocalEvents, setRenderedLocalEvents] = useState(0);
  const [showFifthCards, setShowFifthCards] = useState(false);
  const [showTravelTipsTitle, setShowTravelTipsTitle] = useState(false);
  const [renderedTravelTips, setRenderedTravelTips] = useState(0);
  // Travel Booking links streaming count
  const [renderedTravelBooking, setRenderedTravelBooking] = useState(0);
  const [showTravelBookingTitle, setShowTravelBookingTitle] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const isRenderingRef = useRef(false);

  // 流式渲染逻辑
  useEffect(() => {
    if (!planData) return;

    // 清理之前的渲染状态
    if (isRenderingRef.current) {
      isRenderingRef.current = false;
    }

    // 重置所有状态，准备新的流式渲染
    setShowFirstCards(false);
    setRenderedItineraryDays(0);
    setRenderedItinerarySchedules({});
    setShowSecondCards(false);
    setShowAttractionsTitle(false);
    setRenderedAttractions(0);
    setShowThirdCards(false);
    setShowSeasonalHighlightsTitle(false);
    setRenderedSeasonalHighlights(0);
    setShowFourthCards(false);
    setShowLocalEventsTitle(false);
    setRenderedLocalEvents(0);
    setShowFifthCards(false);
    setShowTravelTipsTitle(false);
    setRenderedTravelTips(0);
    setRenderedTravelBooking(0);
    setShowBackButton(false);
    setShowTravelBookingTitle(false);

    isRenderingRef.current = true;
    const timeouts: NodeJS.Timeout[] = [];
    let currentDelay = 500; // 初始延迟500ms

    // 1. 显示第一个CardRound
    timeouts.push(
      setTimeout(() => {
        setShowFirstCards(true);
      }, currentDelay)
    );
    currentDelay += 800;

    // 2. 逐步显示行程安排
    if (planData.itinerary && planData.itinerary.length > 0) {
      planData.itinerary.forEach((day: any, dayIndex: number) => {
        // 显示新的一天
        timeouts.push(
          setTimeout(() => {
            setRenderedItineraryDays((prev) => Math.max(prev, dayIndex + 1));
            setRenderedItinerarySchedules((prev) => ({
              ...prev,
              [dayIndex]: 0,
            }));
          }, currentDelay)
        );
        currentDelay += 600;

        // 逐步显示这一天的schedule
        if (day.schedule && day.schedule.length > 0) {
          day.schedule.forEach((_: any, scheduleIndex: number) => {
            timeouts.push(
              setTimeout(() => {
                setRenderedItinerarySchedules((prev) => ({
                  ...prev,
                  [dayIndex]: scheduleIndex + 1,
                }));
              }, currentDelay)
            );
            currentDelay += 300;
          });
        }
      });
    }
    currentDelay += 500;

    // 3. 显示第二个CardRound
    timeouts.push(
      setTimeout(() => {
        setShowSecondCards(true);
      }, currentDelay)
    );
    currentDelay += 800;

    // 4. 显示景点推荐标题，然后逐步显示景点推荐
    timeouts.push(
      setTimeout(() => {
        setShowAttractionsTitle(true);
      }, currentDelay)
    );
    currentDelay += 400;

    if (planData.topAttractions && planData.topAttractions.length > 0) {
      planData.topAttractions.forEach((_: any, index: number) => {
        timeouts.push(
          setTimeout(() => {
            setRenderedAttractions(index + 1);
          }, currentDelay)
        );
        currentDelay += 400;
      });
    }
    currentDelay += 500;

    // 5. 显示第三个CardRound
    timeouts.push(
      setTimeout(() => {
        setShowThirdCards(true);
      }, currentDelay)
    );
    currentDelay += 800;

    // 6. 显示季节亮点标题，然后逐步显示季节亮点
    timeouts.push(
      setTimeout(() => {
        setShowSeasonalHighlightsTitle(true);
      }, currentDelay)
    );
    currentDelay += 400;

    if (planData.seasonalHighlights && planData.seasonalHighlights.length > 0) {
      planData.seasonalHighlights.forEach((_: any, index: number) => {
        timeouts.push(
          setTimeout(() => {
            setRenderedSeasonalHighlights(index + 1);
          }, currentDelay)
        );
        currentDelay += 400;
      });
    }
    currentDelay += 500;

    // 7. 显示第四个CardRound
    timeouts.push(
      setTimeout(() => {
        setShowFourthCards(true);
      }, currentDelay)
    );
    currentDelay += 800;

    // 8. 显示当地活动标题，然后逐步显示当地活动
    timeouts.push(
      setTimeout(() => {
        setShowLocalEventsTitle(true);
      }, currentDelay)
    );
    currentDelay += 400;

    if (planData.localEvents && planData.localEvents.length > 0) {
      planData.localEvents.forEach((_: any, index: number) => {
        timeouts.push(
          setTimeout(() => {
            setRenderedLocalEvents(index + 1);
          }, currentDelay)
        );
        currentDelay += 400;
      });
    }
    currentDelay += 500;

    // 9. 显示第五个CardRound
    timeouts.push(
      setTimeout(() => {
        setShowFifthCards(true);
      }, currentDelay)
    );
    currentDelay += 800;

    // 10. 显示旅行贴士标题，然后逐步显示旅行贴士
    timeouts.push(
      setTimeout(() => {
        setShowTravelTipsTitle(true);
      }, currentDelay)
    );
    currentDelay += 400;

    if (planData.travelTips && planData.travelTips.length > 0) {
      planData.travelTips.forEach((_: any, index: number) => {
        timeouts.push(
          setTimeout(() => {
            setRenderedTravelTips(index + 1);
          }, currentDelay)
        );
        currentDelay += 300;
      });
    }
    currentDelay += 500;

    // 11. 逐步显示 Travel Booking 連結（目前固定為 3 個）
    const bookingLinksCount = 3;
    for (let i = 0; i < bookingLinksCount; i++) {
      timeouts.push(
        setTimeout(() => {
          setRenderedTravelBooking(i + 1);
          setShowTravelBookingTitle(true);
        }, currentDelay)
      );
      currentDelay += 300;
    }
    currentDelay += 500;

    // 12. 最后显示返回按钮
    timeouts.push(
      setTimeout(() => {
        setShowBackButton(true);
      }, currentDelay)
    );

    // 清理函数：清除所有定时器
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      isRenderingRef.current = false;
    };
  }, [planData]);

  return {
    showFirstCards,
    renderedItineraryDays,
    renderedItinerarySchedules,
    showSecondCards,
    showAttractionsTitle,
    renderedAttractions,
    showThirdCards,
    showSeasonalHighlightsTitle,
    renderedSeasonalHighlights,
    showFourthCards,
    showLocalEventsTitle,
    renderedLocalEvents,
    showFifthCards,
    showTravelTipsTitle,
    renderedTravelTips,
    renderedTravelBooking,
    showTravelBookingTitle,
    showBackButton,
  };
}
